import React, { useState, useEffect } from "react";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Search,
    Loader2,
    RefreshCw,
    BarChart3,
    Users,
    BookOpen,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { apiCall } from "@/utils/api";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SearchableSelect } from "@/components/ui/searchable-select";

interface AccessLog {
    id: number;
    virtual_class_id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    accessed_at: string;
    ip_address: string | null;
    access_granted: boolean;
}

interface StudentStats {
    email: string;
    first_name: string | null;
    last_name: string | null;
    total_accesses: number;
    classes_accessed: number;
    last_access: string;
    first_access: string;
}

interface OverviewStats {
    total_accesses: number;
    unique_students: number;
    denied_accesses: number;
    top_classes: Array<{
        virtual_class_id: string;
        access_count: number;
        virtual_class: {
            id: string;
            name: string;
        };
    }>;
    timeline: Array<{
        date: string;
        accesses: number;
    }>;
}

interface VirtualClass {
    id: string;
    name: string;
}

const VirtualClassAccessStats: React.FC = () => {
    const [activeView, setActiveView] = useState<string>("overview");
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [refreshing, setRefreshing] = useState<boolean>(false);

    // Overview data
    const [overviewStats, setOverviewStats] = useState<OverviewStats | null>(null);

    // Student stats data
    const [studentStats, setStudentStats] = useState<StudentStats[]>([]);
    const [studentPage, setStudentPage] = useState<number>(1);

    // Class logs data
    const [selectedClassId, setSelectedClassId] = useState<string>("");
    const [virtualClasses, setVirtualClasses] = useState<VirtualClass[]>([]);
    const [classLogs, setClassLogs] = useState<AccessLog[]>([]);
    const [classLogsPage, setClassLogsPage] = useState<number>(1);

    const { token } = useAuth();

    // Fetch virtual classes for dropdown
    useEffect(() => {
        const fetchClasses = async () => {
            try {
                const result = await apiCall<VirtualClass[]>(token, "/virtual-classes");
                setVirtualClasses(result || []);
            } catch (error) {
                console.error("Error fetching classes:", error);
            }
        };
        fetchClasses();
    }, [token]);

    // Fetch overview stats
    const fetchOverview = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (startDate) params.append("start_date", startDate);
            if (endDate) params.append("end_date", endDate);

            const result = await apiCall<OverviewStats>(
                token,
                `/virtual-classes/access-logs/overview?${params.toString()}`
            );
            setOverviewStats(result);
        } catch (error) {
            toast.error("Erreur lors du chargement des statistiques");
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch student stats
    const fetchStudentStats = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (searchTerm) params.append("email", searchTerm);
            if (startDate) params.append("start_date", startDate);
            params.append("per_page", "50");

            const result = await apiCall<{ data: StudentStats[] }>(
                token,
                `/virtual-classes/access-logs/by-student?${params.toString()}`
            );
            setStudentStats(result.data || []);
        } catch (error) {
            toast.error("Erreur lors du chargement des statistiques étudiants");
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch class logs
    const fetchClassLogs = async () => {
        if (!selectedClassId) {
            setClassLogs([]);
            return;
        }

        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (searchTerm) params.append("email", searchTerm);
            if (startDate) params.append("start_date", startDate);
            if (endDate) params.append("end_date", endDate);
            params.append("per_page", "50");

            const result = await apiCall<{ data: AccessLog[] }>(
                token,
                `/virtual-classes/${selectedClassId}/access-logs?${params.toString()}`
            );
            setClassLogs(result.data || []);
        } catch (error) {
            toast.error("Erreur lors du chargement des logs d'accès");
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // Refresh current view
    const handleRefresh = async () => {
        setRefreshing(true);
        if (activeView === "overview") {
            await fetchOverview();
        } else if (activeView === "by-student") {
            await fetchStudentStats();
        } else if (activeView === "by-class") {
            await fetchClassLogs();
        }
        setRefreshing(false);
    };

    // Load data when view or filters change
    useEffect(() => {
        if (activeView === "overview") {
            fetchOverview();
        } else if (activeView === "by-student") {
            fetchStudentStats();
        } else if (activeView === "by-class") {
            fetchClassLogs();
        }
    }, [activeView, selectedClassId]);

    return (
        <div className="flex-1 h-full overflow-y-auto bg-gray-50 pb-10">
            <div className="p-8 max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6 flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-black text-slate-900 mb-1">
                            Statistiques d'Accès
                        </h1>
                        <p className="text-sm text-slate-500">
                            Suivi détaillé des accès aux classes virtuelles
                        </p>
                    </div>
                    <Button
                        onClick={handleRefresh}
                        disabled={refreshing}
                        variant="outline"
                        size="sm"
                    >
                        <RefreshCw
                            className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`}
                        />
                        Actualiser
                    </Button>
                </div>

                {/* Filters */}
                <Card className="mb-6">
                    <CardContent className="pt-6">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                                <Input
                                    type="text"
                                    placeholder="Rechercher par email..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10"
                                />
                            </div>
                            <Input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                placeholder="Date début"
                            />
                            <Input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                placeholder="Date fin"
                            />
                            <Button onClick={handleRefresh} className="w-full">
                                <Search className="h-4 w-4 mr-2" />
                                Filtrer
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Main Content */}
                <Tabs value={activeView} onValueChange={setActiveView}>
                    <TabsList className="grid w-full grid-cols-3 mb-6">
                        <TabsTrigger value="overview">
                            <BarChart3 className="h-4 w-4 mr-2" />
                            Vue d'ensemble
                        </TabsTrigger>
                        <TabsTrigger value="by-student">
                            <Users className="h-4 w-4 mr-2" />
                            Par Étudiant
                        </TabsTrigger>
                        <TabsTrigger value="by-class">
                            <BookOpen className="h-4 w-4 mr-2" />
                            Par Classe
                        </TabsTrigger>
                    </TabsList>

                    {/* Overview Tab */}
                    <TabsContent value="overview">
                        {loading ? (
                            <div className="flex items-center justify-center h-64">
                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                            </div>
                        ) : overviewStats ? (
                            <div className="space-y-6">
                                {/* Stats Cards */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <Card>
                                        <CardHeader className="pb-3">
                                            <CardTitle className="text-sm font-medium text-slate-500">
                                                Total Accès
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <p className="text-3xl font-black text-blue-600">
                                                {overviewStats.total_accesses}
                                            </p>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardHeader className="pb-3">
                                            <CardTitle className="text-sm font-medium text-slate-500">
                                                Étudiants Uniques
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <p className="text-3xl font-black text-emerald-600">
                                                {overviewStats.unique_students}
                                            </p>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardHeader className="pb-3">
                                            <CardTitle className="text-sm font-medium text-slate-500">
                                                Accès Refusés
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <p className="text-3xl font-black text-red-600">
                                                {overviewStats.denied_accesses}
                                            </p>
                                        </CardContent>
                                    </Card>
                                </div>

                                {/* Top Classes */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Top 10 Classes par Accès</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Classe</TableHead>
                                                    <TableHead className="text-right">Nombre d'accès</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {overviewStats.top_classes.map((cls, idx) => (
                                                    <TableRow key={idx}>
                                                        <TableCell className="font-medium">
                                                            {cls.virtual_class?.name || cls.virtual_class_id}
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            {cls.access_count}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {overviewStats.top_classes.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={2} className="text-center text-slate-400">
                                                            Aucune donnée disponible
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </CardContent>
                                </Card>
                            </div>
                        ) : null}
                    </TabsContent>

                    {/* By Student Tab */}
                    <TabsContent value="by-student">
                        <Card>
                            <CardHeader>
                                <CardTitle>Statistiques par Étudiant</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {loading ? (
                                    <div className="flex items-center justify-center h-32">
                                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Nom</TableHead>
                                                <TableHead>Email</TableHead>
                                                <TableHead className="text-right">Accès Total</TableHead>
                                                <TableHead className="text-right">Classes Visitées</TableHead>
                                                <TableHead className="text-right">Dernier Accès</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {studentStats.map((student, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell className="font-medium">
                                                        {student.first_name} {student.last_name}
                                                    </TableCell>
                                                    <TableCell className="text-slate-500 text-sm">
                                                        {student.email}
                                                    </TableCell>
                                                    <TableCell className="text-right font-bold text-blue-600">
                                                        {student.total_accesses}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        {student.classes_accessed}
                                                    </TableCell>
                                                    <TableCell className="text-right text-sm text-slate-500">
                                                        {new Date(student.last_access).toLocaleString()}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {studentStats.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={5} className="text-center text-slate-400">
                                                        Aucune donnée disponible
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* By Class Tab */}
                    <TabsContent value="by-class">
                        <Card className="mb-4">
                            <CardContent className="pt-6">
                                <SearchableSelect
                                    value={selectedClassId}
                                    onValueChange={setSelectedClassId}
                                    placeholder="Sélectionner une classe..."
                                    className="w-full"
                                    options={virtualClasses.map((cls) => ({
                                        value: cls.id,
                                        label: cls.name,
                                    }))}
                                />
                            </CardContent>
                        </Card>

                        {selectedClassId && (
                            <Card>
                                <CardHeader>
                                    <CardTitle>Logs d'Accès</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {loading ? (
                                        <div className="flex items-center justify-center h-32">
                                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                        </div>
                                    ) : (
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Nom</TableHead>
                                                    <TableHead>Email</TableHead>
                                                    <TableHead>Adresse IP</TableHead>
                                                    <TableHead className="text-right">Date/Heure</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {classLogs.map((log) => (
                                                    <TableRow key={log.id}>
                                                        <TableCell className="font-medium">
                                                            {log.first_name} {log.last_name}
                                                        </TableCell>
                                                        <TableCell className="text-slate-500 text-sm">
                                                            {log.email}
                                                        </TableCell>
                                                        <TableCell className="font-mono text-xs">
                                                            {log.ip_address || 'N/A'}
                                                        </TableCell>
                                                        <TableCell className="text-right text-sm text-slate-500">
                                                            {new Date(log.accessed_at).toLocaleString()}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {classLogs.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={4} className="text-center text-slate-400">
                                                            Aucun log d'accès pour cette classe
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    )}
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
};

export default VirtualClassAccessStats;
